import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Trash2, Phone, Mail, Building2, ArrowRight, CheckCircle2, Sparkles, Webhook,
} from "lucide-react";
import { toast } from "sonner";

const STAGES = ["new", "qualified", "proposal", "negotiation", "won", "lost"];
const STAGE_CLS = {
  new: "bg-slate-100 text-slate-600",
  qualified: "bg-blue-50 text-blue-700",
  proposal: "bg-amber-50 text-amber-700",
  negotiation: "bg-indigo-50 text-indigo-700",
  won: "bg-emerald-50 text-emerald-700",
  lost: "bg-red-50 text-red-700",
};
const PRIORITY_CLS = {
  low: "text-slate-500", medium: "text-amber-600", high: "text-red-600",
};
const SOURCE_LABEL = {
  manual: "Manual", indiamart: "IndiaMART", website: "Website", referral: "Referral", other: "Other",
};
const ACT_TYPES = ["call", "email", "meeting", "site_visit", "note"];

export default function CrmPage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [items, setItems] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stageFilter, setStageFilter] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [actOpen, setActOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    company: "", contact_person: "", phone: "", email: "", city: "",
    source: "manual", status: "new", priority: "medium", estimated_value: 0,
    product_interest: "", notes: "", expected_close_date: "",
  });
  const [convertForm, setConvertForm] = useState({
    customer_code: "", customer_name: "", gstin: "", address: "",
    po_reference: "", expected_delivery_date: "",
    lines: [{ item_id: "", qty: 1, price: 0 }],
  });
  const [actForm, setActForm] = useState({ type: "call", subject: "", notes: "", due_date: "" });

  const fgItems = useMemo(() => items.filter((i) => i.type !== "RAW"), [items]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  const load = async () => {
    const [l, i] = await Promise.all([api.get("/leads"), api.get("/items")]);
    setLeads(l.data);
    setItems(i.data);
    if (selected) {
      const found = l.data.find((x) => x.id === selected.id);
      setSelected(found || null);
      if (found) loadActivities(found.id);
    }
  };
  const loadActivities = async (id) => {
    const { data } = await api.get(`/leads/${id}/activities`);
    setActivities(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selected) loadActivities(selected.id); }, [selected?.id]);

  const onSelect = (l) => { setSelected(l); };

  const create = async () => {
    if (busy) return;
    if (!form.company.trim()) return toast.error("Company is required");
    setBusy(true);
    try {
      await api.post("/leads", {
        ...form,
        expected_close_date: form.expected_close_date ? new Date(form.expected_close_date).toISOString() : null,
      });
      toast.success("Lead created");
      setOpen(false);
      setForm({ ...form, company: "", contact_person: "", phone: "", email: "", city: "", estimated_value: 0, product_interest: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const updateStatus = async (l, status) => {
    try {
      await api.patch(`/leads/${l.id}`, { status });
      toast.success(`${l.code} → ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.code}?`)) return;
    try {
      await api.delete(`/leads/${l.id}`);
      if (selected?.id === l.id) setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addActLine = () => setConvertForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", qty: 1, price: 0 }] }));
  const setActLine = (i, p) => setConvertForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...p } : l) }));
  const rmActLine = (i) => setConvertForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const openConvert = () => {
    if (!selected) return;
    setConvertForm({
      customer_code: `C-${selected.code}`,
      customer_name: selected.company,
      gstin: "", address: selected.city || "",
      po_reference: "", expected_delivery_date: "",
      lines: [{ item_id: fgItems[0]?.id || "", qty: 1, price: 0 }],
    });
    setConvertOpen(true);
  };

  const convert = async () => {
    setBusy(true);
    try {
      const payload = {
        ...convertForm,
        expected_delivery_date: convertForm.expected_delivery_date ? new Date(convertForm.expected_delivery_date).toISOString() : null,
        so_lines: convertForm.lines.filter((l) => l.item_id && l.qty > 0),
      };
      if (!payload.so_lines.length) return toast.error("Add at least one order line");
      const { data } = await api.post(`/leads/${selected.id}/convert-to-so`, payload);
      toast.success(`Converted · ${data.sales_order.code} created`);
      setConvertOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Convert failed"); }
    finally { setBusy(false); }
  };

  const createActivity = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/leads/${selected.id}/activities`, {
        ...actForm,
        due_date: actForm.due_date ? new Date(actForm.due_date).toISOString() : null,
      });
      toast.success("Activity logged");
      setActOpen(false);
      setActForm({ type: "call", subject: "", notes: "", due_date: "" });
      loadActivities(selected.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActivity = async (a) => {
    try {
      await api.patch(`/activities/${a.id}`, { completed: !a.completed });
      loadActivities(selected.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = stageFilter === "ALL" ? leads : leads.filter((l) => l.status === stageFilter);
  const stats = STAGES.map((s) => ({ stage: s, count: leads.filter((l) => l.status === s).length, value: leads.filter((l) => l.status === s).reduce((a, l) => a + (l.estimated_value || 0), 0) }));

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales · CRM</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Leads Pipeline
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manual entry + auto-captured from IndiaMART. Convert won leads into a Transfer Order.</p>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-lead-btn"><Plus className="w-4 h-4 mr-1.5" />New Lead</Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>New Lead</SheetTitle>
              <SheetDescription>Capture a new sales opportunity.</SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lead-company-input" /></div>
                <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Est. Value (₹)</Label><Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: parseFloat(e.target.value || 0) })} /></div>
                <div className="col-span-2"><Label>Product Interest</Label><Input value={form.product_interest} onChange={(e) => setForm({ ...form, product_interest: e.target.value })} /></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div><Label>Expected Close</Label><Input type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} /></div>
              </div>
            </div>
            <SheetFooter className="mt-6">
              <Button onClick={create} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="create-lead-btn">Create Lead</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      {/* Stage tiles */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3" data-testid="stage-tiles">
        <button onClick={() => setStageFilter("ALL")} className={`text-left bg-white border rounded-lg p-3 hover:border-blue-300 ${stageFilter === "ALL" ? "border-blue-600 ring-1 ring-blue-100" : "border-slate-200"}`}>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">All</div>
          <div className="mt-1 text-xl font-extrabold mono">{leads.length}</div>
        </button>
        {stats.map((s) => (
          <button key={s.stage} onClick={() => setStageFilter(s.stage)} className={`text-left bg-white border rounded-lg p-3 hover:border-blue-300 ${stageFilter === s.stage ? "border-blue-600 ring-1 ring-blue-100" : "border-slate-200"}`} data-testid={`stage-tile-${s.stage}`}>
            <div className={`text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full inline-block ${STAGE_CLS[s.stage]}`}>{s.stage}</div>
            <div className="mt-1 text-xl font-extrabold mono">{s.count}</div>
            <div className="text-[10px] text-slate-500 mono">₹ {s.value.toLocaleString()}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>Leads</span><span className="text-xs text-slate-500 mono">{filtered.length}</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {filtered.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-leads">No leads.</div>}
            {filtered.map((l) => (
              <button key={l.id} onClick={() => onSelect(l)} data-testid={`lead-row-${l.code}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === l.id ? "bg-blue-50/60" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{l.code}</div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STAGE_CLS[l.status]}`}>{l.status}</span>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-800 truncate">{l.company}</div>
                <div className="text-xs text-slate-500 truncate">
                  {l.contact_person || "—"} · <span className={PRIORITY_CLS[l.priority]}>{l.priority}</span>
                  {l.source !== "manual" && <span className="ml-1 text-[10px] uppercase bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{SOURCE_LABEL[l.source]}</span>}
                </div>
                {l.estimated_value > 0 && <div className="text-xs text-slate-600 mono mt-0.5">₹ {l.estimated_value.toLocaleString()}</div>}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="lead-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <Building2 className="w-10 h-10 text-slate-300" /> Select a lead to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>{selected.company}</h2>
                  <div className="text-xs text-slate-500 mt-1">
                    {selected.contact_person && <>{selected.contact_person} · </>}
                    {selected.city || "—"} · <span className={PRIORITY_CLS[selected.priority]}>{selected.priority} priority</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
                    {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-1 hover:text-blue-700"><Phone className="w-3 h-3" /> {selected.phone}</a>}
                    {selected.email && <a href={`mailto:${selected.email}`} className="flex items-center gap-1 hover:text-blue-700"><Mail className="w-3 h-3" /> {selected.email}</a>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STAGE_CLS[selected.status]}`}>{selected.status}</span>
                  {selected.estimated_value > 0 && <div className="mono font-bold text-lg">₹ {selected.estimated_value.toLocaleString()}</div>}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap" data-testid="lead-actions">
                <Select value={selected.status} onValueChange={(v) => updateStatus(selected, v)}>
                  <SelectTrigger className="w-40 h-9" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!selected.so_id && !["won", "lost"].includes(selected.status) && (
                  <Button onClick={openConvert} className="bg-blue-600 hover:bg-blue-700" data-testid="convert-lead-btn">
                    <ArrowRight className="w-4 h-4 mr-1.5" /> Convert to SO
                  </Button>
                )}
                {selected.so_code && (
                  <div className="flex items-center gap-1 text-xs text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" /> Converted to <span className="mono font-semibold">{selected.so_code}</span>
                  </div>
                )}
                <Button variant="outline" onClick={() => setActOpen(true)} data-testid="new-activity-btn">
                  <Plus className="w-4 h-4 mr-1" /> Log Activity
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(selected)} className="ml-auto" data-testid="delete-lead-btn">
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>

              {selected.product_interest && (
                <div className="border-l-2 border-blue-300 pl-3 text-sm">
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Product Interest</div>
                  <div className="text-slate-700">{selected.product_interest}</div>
                </div>
              )}
              {selected.notes && (
                <div className="border-l-2 border-slate-200 pl-3 text-sm italic text-slate-600">{selected.notes}</div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Activities</div>
                  <span className="text-xs text-slate-500 mono">{activities.length}</span>
                </div>
                <div className="space-y-2">
                  {activities.length === 0 && <div className="text-xs text-slate-500 py-4 text-center">No activities yet.</div>}
                  {activities.map((a) => (
                    <div key={a.id} className="border border-slate-200 rounded-md p-3" data-testid={`activity-${a.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-xs uppercase tracking-wider text-slate-500">{a.type.replace("_", " ")} · {a.by}</div>
                          {a.subject && <div className="text-sm font-medium text-slate-800 mt-0.5">{a.subject}</div>}
                          {a.notes && <div className="text-xs text-slate-600 mt-1">{a.notes}</div>}
                          {a.due_date && <div className="text-[10px] text-slate-500 mono mt-1">Due {new Date(a.due_date).toLocaleDateString()}</div>}
                        </div>
                        <input type="checkbox" checked={!!a.completed} onChange={() => toggleActivity(a)} className="mt-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity sheet */}
      <Sheet open={actOpen} onOpenChange={setActOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Log Activity</SheetTitle>
            <SheetDescription>Calls, emails, meetings or site visits with this lead.</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={actForm.type} onValueChange={(v) => setActForm({ ...actForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ACT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Subject</Label><Input value={actForm.subject} onChange={(e) => setActForm({ ...actForm, subject: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea rows={3} value={actForm.notes} onChange={(e) => setActForm({ ...actForm, notes: e.target.value })} /></div>
            <div><Label>Due Date</Label><Input type="date" value={actForm.due_date} onChange={(e) => setActForm({ ...actForm, due_date: e.target.value })} /></div>
          </div>
          <SheetFooter className="mt-6"><Button onClick={createActivity} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="save-activity-btn">Save</Button></SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Convert sheet */}
      <Sheet open={convertOpen} onOpenChange={setConvertOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Convert Lead → Transfer Order</SheetTitle>
            <SheetDescription>Creates a customer (if new) and a draft transfer order.</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer Code</Label><Input value={convertForm.customer_code} onChange={(e) => setConvertForm({ ...convertForm, customer_code: e.target.value })} /></div>
              <div><Label>Customer Name</Label><Input value={convertForm.customer_name} onChange={(e) => setConvertForm({ ...convertForm, customer_name: e.target.value })} /></div>
              <div><Label>GSTIN</Label><Input value={convertForm.gstin} onChange={(e) => setConvertForm({ ...convertForm, gstin: e.target.value })} /></div>
              <div><Label>PO Reference</Label><Input value={convertForm.po_reference} onChange={(e) => setConvertForm({ ...convertForm, po_reference: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={convertForm.address} onChange={(e) => setConvertForm({ ...convertForm, address: e.target.value })} /></div>
              <div><Label>Expected Delivery</Label><Input type="date" value={convertForm.expected_delivery_date} onChange={(e) => setConvertForm({ ...convertForm, expected_delivery_date: e.target.value })} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-700">Order Lines</div>
                <Button size="sm" variant="outline" onClick={addActLine}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
              </div>
              <div className="space-y-2">
                {convertForm.lines.map((l, idx) => (
                  <div key={`cv-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      <Label className="text-[10px] uppercase">Item</Label>
                      <Select value={l.item_id} onValueChange={(v) => setActLine(idx, { item_id: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {fgItems.map((it) => <SelectItem key={it.id} value={it.id}>{it.code} — {it.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2"><Label className="text-[10px] uppercase">Qty</Label><Input type="number" value={l.qty} onChange={(e) => setActLine(idx, { qty: parseFloat(e.target.value || 0) })} /></div>
                    <div className="col-span-3"><Label className="text-[10px] uppercase">Price</Label><Input type="number" value={l.price} onChange={(e) => setActLine(idx, { price: parseFloat(e.target.value || 0) })} /></div>
                    <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => rmActLine(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SheetFooter className="mt-6"><Button onClick={convert} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-convert-btn">Convert to SO</Button></SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
